/**
 * Use Cases: Program CRUD Operations
 * Business logic for program detail, create, update, and delete.
 */

import type {
  ProductUploadResult,
  ProgramDetail,
  ProgramWritePayload,
  WhitelistUploadResult,
} from "../model/ProgramsModel";
import type { IProgramsRepository } from "../repository/IProgramsRepository";
import { programsContainer, TYPES } from "../../di/ProgramsModule";

/**
 * Fetch detailed information for a specific program.
 */
export const FetchProgramDetailUseCase = async (
  programId: string,
): Promise<ProgramDetail> => {
  const repository = programsContainer.get<IProgramsRepository>(
    TYPES.IProgramsRepository,
  );

  return repository.fetchDetail(programId);
};

/**
 * Create a new program.
 * @returns The ID of the newly created program.
 */
export const CreateProgramUseCase = async (
  payload: ProgramWritePayload,
): Promise<string> => {
  const repository = programsContainer.get<IProgramsRepository>(
    TYPES.IProgramsRepository,
  );

  return repository.create(payload);
};

/**
 * Update an existing program.
 */
export const UpdateProgramUseCase = async (
  programId: string,
  payload: ProgramWritePayload,
): Promise<void> => {
  const repository = programsContainer.get<IProgramsRepository>(
    TYPES.IProgramsRepository,
  );

  await repository.update(programId, payload);
};

/**
 * Delete a program.
 */
export const DeleteProgramUseCase = async (programId: string): Promise<void> => {
  const repository = programsContainer.get<IProgramsRepository>(
    TYPES.IProgramsRepository,
  );

  await repository.delete(programId);
};

/**
 * Upload whitelist file and get preview with temporary file ID.
 */
export const UploadWhitelistFileUseCase = async (
  file: File,
): Promise<WhitelistUploadResult> => {
  const repository = programsContainer.get<IProgramsRepository>(
    TYPES.IProgramsRepository,
  );

  return repository.uploadWhitelistFile(file);
};

/**
 * Upload product incentive file and get preview with temporary file ID.
 */
export const UploadProductFileUseCase = async (
  file: File,
): Promise<ProductUploadResult> => {
  const repository = programsContainer.get<IProgramsRepository>(
    TYPES.IProgramsRepository,
  );

  return repository.uploadProductFile(file);
};
